import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { Listbox, Combobox } from '@headlessui/react';

import { AjaxListingContext, AjaxListingPagination } from '$partials/base/ajax-listing.jsx';
import { Footer, Header, Meta } from '$partials/global';
import { useAjaxListing } from '$hooks/ajax-listing';
import { ArticleStandardListingItem } from '$partials/listings/items';
import { useLocale } from '$contexts/locale.jsx';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

const Marker = ({ colour = '#FC5200' }) => {
    return (
        <svg
            className='inline'
            style={{ width: '8px', height: '8px' }}
            viewBox='0 0 8 8'
            xmlns='http://www.w3.org/2000/svg'>
            <circle
                cx='4'
                cy='4'
                fill={colour}
                r='4' />
        </svg>
    );
};

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const SearchPage = ({ data, domain }) => {
    const { current: locale, translate: t } = useLocale();
    const [filter, setFilter] = useState(data.topics?.find(topic => topic?.slug === data?.items?.filters?.topic));
    const inputRef = useRef();
    const [input, setInput] = useState('');
    const [query, setQuery] = useState(data.search?.term || '');
    const [term, setTerm] = useState(data.search?.term || '');

    const options = data.search?.keywords?.filter((option) =>
        option.title
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''))
    );

    const handleKeyPress = (event) => {
        // enter
        if (event.keyCode === 13) {
            event.preventDefault();
            setTerm(query);
        }
    };

    const page = data?.page;

    const listing = useAjaxListing({
        items: data?.items?.items,
        page: data?.items?.page || 1,
        path: '/api/v1/search',
        totalItems: data?.items?.total,
        totalPages: data?.items?.totalPages,
        query: { locale: locale?.code, topic: filter?.slug, term: term }
    });

    return (
        <>
            <Meta page={page} />

            <main
                className='bg-grey min-h-screen'
                id='main-content'>
                <div className='layout-container'>
                    <Header
                        menu={data?.navigation}
                        search={data?.search}/>
                    <article>

                        {/* Search bar */}
                        <form className='relative width-100 mt-60 md:mt-90'>
                            <div className='px-10 sm:px-20 relative'>
                                <Combobox
                                    onChange={(val)=>{
                                        setInput(val.title);
                                        setTerm(val.title);
                                    }}
                                    value={input}>
                                    <fieldset className='flex bg-white rounded-l'>
                                        <Combobox.Input
                                            className='h-[45px] w-full border-0 rounded-l focus:ring-0 pl-20 py-10 type-style-7 '
                                            displayValue={()=> term}
                                            onChange={(event) => {
                                                setQuery(event.target.value);}}
                                            onKeyUp={handleKeyPress}
                                            ref={inputRef}
                                            value={query}/>
                                        <button
                                            className='inset-y-0 flex items-center bg-orange text-white px-20 rounded  type-style-7'
                                            onClick={() => {
                                                setTerm(query);
                                            }}
                                            type='button'>
                                            Go!
                                        </button>
                                    </fieldset>

                                    <Combobox.Options>
                                        {options.length > 0 && query !== '' &&  (
                                            options.map((option) => (
                                                <Combobox.Option
                                                    className={({ active }) =>
                                                        `relative cursor-pointer select-none py-20 px-20  type-style-7 rounded mt-1 ${
                                                            active ? 'bg-orange text-white' : 'text-orange bg-white'
                                                        }`
                                                    }
                                                    key={option.id}
                                                    value={option}>
                                                    <span className=''>
                                                        {option.title}
                                                    </span>
                                                </Combobox.Option>
                                            ))
                                        )}
                                    </Combobox.Options>
                                </Combobox>
                            </div>
                        </form>

                        {/* Header */}
                        <header className='px-10 sm:px-20 pt-60 md:pt-80'>
                            <h1 className='type-style-2 w-full sm:w-4/6 md:w-10/12'>{listing.totalItems} {t('Results')}</h1>
                            {data.topics?.length > 0 && (
                                <div className='type-style-7 mt-20 md:mt-44 mb-60 md:mb-70'>
                                    <Listbox
                                        onChange={setFilter}
                                        value={filter}>
                                        {({ open }) => {
                                            return (
                                                <div className='relative w-full sm:w-max'>
                                                    <Listbox.Button className={`select ${open ? 'open w-full sm:w-auto text-black' : 'text-orange'} flex items-center bg-white rounded type-style-7 `}>
                                                        {filter?.title}
                                                    </Listbox.Button>
                                                    <div
                                                        className={'absolute w-full bg-white'}>
                                                        <Listbox.Options className='flex flex-col z-10 rounded-b bg-white absolute w-full'>
                                                            {data.topics.map((topic, idx) => {
                                                                if (topic.slug === filter?.slug) { return null;}
                                                                return (
                                                                    <Listbox.Option
                                                                        className='px-20 flex items-center text-black hover:text-white hover:bg-orange type-style-7  py-15 cursor-pointer w-full'
                                                                        key={idx}
                                                                        value={topic}>
                                                                        {topic.title}
                                                                    </Listbox.Option>
                                                                );})}
                                                        </Listbox.Options>
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    </Listbox>
                                </div>
                            )}
                        </header>

                        {/* Listing */}
                        <AjaxListingContext.Provider value={listing}>
                            <section
                                aria-busy={listing.loading}
                                aria-label='Articles'
                                className='flex flex-wrap mt-40'
                                role='feed'>
                                {listing?.items?.map((item, i) => {
                                    return (
                                        <article
                                            aria-label={item.title}
                                            aria-posinset={i + 1}
                                            aria-setsize={listing.totalItems}
                                            className={'flex flex-col w-full sm:w-6/12 md:w-4/12 pb-50 md:pb-70 px-10 sm:px-20'}
                                            key={`${item.sys?.id}-${i}`}
                                            tabIndex={-1}>
                                            <ArticleStandardListingItem {...item} />
                                        </article>
                                    );
                                })}

                                <div className='border-b border-dark-grey mx-10 sm:mx-20 w-full' />
                            </section>
                            {listing.totalPages > 1 && (
                                <div className='w-full px-10 sm:px-20 mt-30 mb-20 sm:mb-0 flex items-start'>
                                    <AjaxListingPagination
                                        current={listing.page}
                                        totalPages={listing.totalPages}/>
                                </div>
                            )}
                        </AjaxListingContext.Provider>
                    </article>

                    <Footer
                        domain={domain}
                        menu={data?.footer} />
                </div>
            </main>
        </>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

Marker.propTypes = {
    colour: PropTypes.string,
};

const propTypes = {
    data: PropTypes.shape({
        items: PropTypes.shape({
            filters: PropTypes.object,
            items: PropTypes.array,
            page: PropTypes.number,
            total: PropTypes.number,
            totalPages: PropTypes.number,
        }),
        page: PropTypes.object,
        topics: PropTypes.array,
        footer: PropTypes.shape({
            items: PropTypes.array,
        }),
        navigation: PropTypes.object,
        search: PropTypes.object,
    }),
    domain: PropTypes.string,
};

SearchPage.propTypes = propTypes;

export default SearchPage;
